import { compareDesc } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CUSTOM_CONFIRM_TYPES, CustomConfirm } from "components";
import { AddPinModal } from "components/PinModal/components";
import { useGetPinOptionsDropdownOptions } from "components/PinModal/hooks";
import { PinOverview } from "modules/AppointmentDetails/components/Interventions/components";

import type { PinModelWithHistory } from "components";
import type { Dealer, Pin } from "models";
import type { DeletePinPayload } from "modules/AppointmentDetails/components/Interventions/hooks";
import type { OrderedGroupedCheck } from "modules/AppointmentDetails/hooks";

type PinProps = {
  pin: PinModelWithHistory;
  isOpen: boolean;
  dealer?: Dealer | null;
  check?: OrderedGroupedCheck;
  readOnly?: boolean;
  isSavingPin?: boolean;
  isDeletingPin?: boolean;
  onClose: () => void;
  onSavePin?: (payload: Pin) => Promise<void>;
  onDeletePin?: (payload: DeletePinPayload) => Promise<void>;
};

export const PinModal = ({ pin, dealer, isOpen, onClose, onSavePin, onDeletePin, isSavingPin = false, isDeletingPin = false, readOnly = false, check }: PinProps) => {
  const { t } = useTranslation();

  const { pinOrderOptions, pinStatusOptions, pinTypeOptions, pinWarrantyOptions } = useGetPinOptionsDropdownOptions(dealer);
  const latestPinData = pin.history.sort((a: Pin, b: Pin) => compareDesc(a.created_on, b.created_on))[0]; // TODO: should not be needed as sort need to happen in select, review all usecase of PinModal before removing it

  const [showPrintOverview, setShowPrintOverview] = useState(false);
  const [deleteConfirmationNote, setDeleteConfirmationNote] = useState("");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const openDeleteConfirmationModal = () => {
    setDeleteConfirmationNote("");
    setShowDeleteConfirmation(true);
  };

  const handleDeletePin = async () => {
    if (!onDeletePin) return;

    try {
      await onDeletePin({
        note: deleteConfirmationNote,
        ...(pin.intervention_id && { intervention_id: pin.intervention_id }),
        ...(pin.question_result_id && { question_result_id: pin.question_result_id })
      });
      onClose();
    } catch (error) {
      console.error("Failed to delete pin:", error);
    }
  };

  return (
    <div className="PinModal">
      <AddPinModal
        pin={pin}
        dealer={dealer}
        isOpen={isOpen}
        onClose={onClose}
        readOnly={readOnly}
        onSavePin={onSavePin}
        isSavingPin={isSavingPin}
        isDeleteButtonDisabled={isDeletingPin}
        openDeleteConfirmationModal={openDeleteConfirmationModal}
        openPrintOverviewModal={() => setShowPrintOverview(true)}
      />
      <CustomConfirm
        isLoading={isDeletingPin}
        note={deleteConfirmationNote}
        isOpen={showDeleteConfirmation}
        type={CUSTOM_CONFIRM_TYPES.Danger}
        customClass="CancelAppointmentModal"
        isDisabled={!deleteConfirmationNote || isDeletingPin}
        customTitle={t("v8_are_you_sure", "Are you sure?")}
        confirmMandatoryNote={t("v8_reason_for_deleting_pinned_item", "Reason for deleting pinned item.")}
        confirmMsg={t("v8_delete_confirm_message", "Are you sure that you want to delete this? You can't undo this action.")}
        handleConfirm={handleDeletePin}
        handleCancel={() => setShowDeleteConfirmation(false)}
        onChangeNote={e => setDeleteConfirmationNote(e.target.value)}
      />
      <PinOverview
        pin={latestPinData}
        pinLog={pin.history}
        pinTypeOptions={pinTypeOptions}
        pinOrderOptions={pinOrderOptions}
        pinStatusOptions={pinStatusOptions}
        pinWarrantyOptions={pinWarrantyOptions}
        showPrintOverview={showPrintOverview}
        appointment={pin.appointment}
        intervention={pin.intervention}
        check={check}
        onClose={() => setShowPrintOverview(false)}
      />
    </div>
  );
};
